import { cn } from "@/lib/utils";
import { Shield } from "lucide-react";

import { tAuto } from "@/lib/tAuto";
// ==========================================
// Custom Icons for Premium Badges
// ==========================================

/**
 * Custom Wavy Badge Check icon matching the verified seal style.
 * Uses exact mathematical path of the wavy circle and renders a crisp white checkmark.
 */
export const WavyCheckIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn("shrink-0", className)}
  >
    <path
      d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
      fill="#E8513D"
      stroke="#E8513D"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    <path
      d="m9 12.2 2 2 4.5-4.5"
      stroke="white"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const OutlineWavyCheckIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn("shrink-0", className)}
  >
    <path
      d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="m9 12.2 2 2 4.5-4.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==========================================
// Size Map Configurations
// ==========================================

const sizeConfigs = {
  xs: {
    badge: "h-5 px-2 text-[9px] gap-1",
    icon: "w-3 h-3",
  },
  sm: {
    badge: "h-6 px-2.5 text-[11px] gap-1",
    icon: "w-3.5 h-3.5",
  },
  md: {
    badge: "h-7 px-3 text-xs gap-1.5",
    icon: "w-3.5 h-3.5",
  },
  lg: {
    badge: "h-8 px-4 text-sm gap-2",
    icon: "w-4.5 h-4.5",
  },
};

// ==========================================
// 1. Verified Badge (Selo Verificado)
// ==========================================

interface VerifiedBadgeProps {
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "pill" | "icon" | "outline";
  className?: string;
}

export const VerifiedBadge = ({ size = "sm", variant = "pill", className }: VerifiedBadgeProps) => {
  const config = sizeConfigs[size];

  if (variant === "icon") {
    const iconSizeMap = {
      xs: "w-4 h-4",
      sm: "w-5 h-5",
      md: "w-6 h-6",
      lg: "w-7 h-7",
    };
    return (
      <div
        className={cn(
          "rounded-full bg-white dark:bg-[#1C1C1E] flex items-center justify-center shadow-md border border-[#E8513D]/20 p-[2px]",
          iconSizeMap[size],
          className
        )}
        title={tAuto("badges.verified", "Verificado")}
      >
        <WavyCheckIcon className="w-full h-full" />
      </div>
    );
  }

  // Capsule (pill or outline) - white background with dark brown text matching the design
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center font-semibold rounded-full bg-white text-[#4A3D36] select-none shadow-sm dark:bg-[#2C2C2E] dark:text-[#EAE5E0] whitespace-nowrap shrink-0",
        config.badge,
        className
      )}
    >
      <OutlineWavyCheckIcon className={config.icon} />
      <span className="tracking-wide">{tAuto("badges.verified", "Verificado")}</span>
    </div>
  );
};

// ==========================================
// 2. Space Type Badge (Selo do Tipo de Espaço)
// ==========================================

interface SpaceTypeBadgeProps {
  type: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

export const SpaceTypeBadge = ({ type, size = "sm", className }: SpaceTypeBadgeProps) => {
  const config = sizeConfigs[size];
  const displayType = type ? type.toLowerCase() : "espaço";

  // Capsule - white background with dark brown text matching the design
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center font-semibold rounded-full bg-white text-[#4A3D36] select-none shadow-sm dark:bg-[#2C2C2E] dark:text-[#EAE5E0] whitespace-nowrap shrink-0 capitalize",
        config.badge,
        className
      )}
    >
      <span className="tracking-wide">{displayType}</span>
    </div>
  );
};

// ==========================================
// 3. Insured Badge (Selo de Assegurado / Seguro)
// ==========================================

interface InsuredBadgeProps {
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

export const InsuredBadge = ({ size = "sm", className }: InsuredBadgeProps) => {
  const sizeMap = {
    xs: "w-5 h-5",
    sm: "w-6 h-6",
    md: "w-7 h-7",
    lg: "w-8 h-8",
  };
  
  return (
    <div className={cn("relative shrink-0 flex items-center justify-center aspect-square select-none", sizeMap[size], className)}>
      <img
        src="/premium-badge.png"
        alt={tAuto("auto.components_verifiedbadge.1", "Imóvel Premium")}
        className="w-full h-full object-contain block"
        title={tAuto("auto.components_verifiedbadge.2", "Imóvel Premium")}
        loading="lazy"
      />
    </div>
  );
};

export default VerifiedBadge;



